import { useState } from 'react';
import { useCopilot } from '../../api/copilot';
import { callSafely } from './util';

/**
 * A question that went unanswered, with a way to ask it again.
 *
 * The provider gives up waiting after a while and says so here, instead of
 * letting "thinking" quietly disappear — a silent give-up is indistinguishable
 * from an answer that was never going to come. The question is quoted so the
 * reader knows which one, when several were asked.
 *
 * Retry stops working once pressed: the provider re-asks and the next give-up
 * (if any) arrives as a fresh card, so this one has done its job. It says so
 * with `aria-disabled` rather than `disabled`, which would drop keyboard focus
 * from the button just pressed (the composer's Send works the same way). The title is
 * the provider's when it gave one — a question that was never sent is a
 * different situation from one that went unanswered.
 */
export default function RetryCard({ message }) {
  const { retry } = useCopilot();
  const [sent, setSent] = useState(false);
  if (!message) return null;
  const card = message.card || {};
  const question = card.question || '';
  /* The provider marks the card done once it has re-asked, which is what keeps
     the dock's copy and the drawer's copy of this card in agreement. */
  const done = sent || card.status === 'done';

  return (
    <div className="cp-card cp-retry">
      <div className="cp-card-row">
        <b className="cp-card-title">{card.title || 'No answer yet'}</b>
        <button
          type="button"
          className="btn cp-btn"
          aria-disabled={done}
          onClick={() => {
            if (done) return;
            setSent(true);
            callSafely(retry, message.id);
          }}
        >
          {done ? 'Asked again' : 'Retry'}
        </button>
      </div>
      {question && <p className="cp-card-detail">“{question}”</p>}
      {card.detail && <p className="cp-card-detail">{card.detail}</p>}
    </div>
  );
}
